import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Wallet } from "lucide-react";
import { usePortfolioData } from "@/hooks/usePortfolioData";
import PortfolioSummaryCards from "@/components/portfolio/PortfolioSummaryCards";
import PortfolioPerformanceAnalytics from "@/components/portfolio/PortfolioPerformanceAnalytics";
import PortfolioHoldings from "@/components/portfolio/PortfolioHoldings";
import PerformanceTracker from "@/components/portfolio/PerformanceTracker";
import ShareCardSection from "@/components/portfolio/ShareCardSection";
import AITradeJournal from "@/components/portfolio/AITradeJournal";
import BrokerConnections from "@/components/portfolio/BrokerConnections";
import HoldingsInsights from "@/components/portfolio/HoldingsInsights";
import PortfolioTrendChart from "@/components/portfolio/PortfolioTrendChart";
import BrokerOrders from "@/components/portfolio/BrokerOrders";

export default function Portfolio() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    portfolio, analytics, loading, trades, perfStats,
    journal, journalLoading, shareCard, shareLoading,
    removeAsset, closeTrade, deleteTrade, fetchJournal, generateShareCard,
  } = usePortfolioData();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("snaptrade")) return;
    toast.success("Brokerage connection updated — holdings will appear below");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <Wallet className="w-10 h-10 text-[#00E0FF]" />
            <h1 className="text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Unbounded' }}>
              MY PORTFOLIO
            </h1>
          </div>
          <p className="text-gray-400">Track your investments and performance</p>
        </motion.div>

        <PortfolioSummaryCards summary={portfolio.summary} />
        {isAuthenticated && <PortfolioTrendChart />}
        <PortfolioPerformanceAnalytics analytics={analytics} />
        <PortfolioHoldings
          portfolio={portfolio}
          loading={loading}
          onRemove={removeAsset}
          onExplore={() => navigate('/dashboard')}
        />

        {isAuthenticated && (
          <PerformanceTracker
            perfStats={perfStats}
            trades={trades}
            onClose={closeTrade}
            onDelete={deleteTrade}
          />
        )}

        {isAuthenticated && <BrokerConnections />}

        {isAuthenticated && <HoldingsInsights />}

        {isAuthenticated && <BrokerOrders />}

        {isAuthenticated && trades.length > 0 && (
          <ShareCardSection
            shareCard={shareCard}
            shareLoading={shareLoading}
            onGenerate={generateShareCard}
          />
        )}

        {isAuthenticated && (
          <AITradeJournal
            journal={journal}
            journalLoading={journalLoading}
            onAnalyze={fetchJournal}
          />
        )}
      </main>
    </div>
  );
}
